import type { HeightFrame, ProfilePoint } from "../types";
import { stabilityBand } from "./physics";

/**
 * The stability field, sampled continuously rather than drawn as one flat
 * rectangle per hour per layer.
 *
 * The mosaic version puts a hard vertical edge at every hour boundary, which
 * reads as though the atmosphere changes discontinuously on the hour. It does
 * not: those edges are the sampling grid showing through. Sampling between
 * columns and between levels, then quantising each pixel to its band, keeps
 * the boundaries between regimes — which are the actual information — while
 * letting them fall where the data puts them.
 *
 * Rendered to a small raster and scaled by the browser. A vector version of
 * the same picture needs tens of thousands of paths; this is a few kilobytes,
 * and every other mark on the chart stays vector.
 */

export interface FieldLayer {
  height: number;
  rate: number;
}

/** Layer lapse rates at their mid-heights, ascending. */
export function lapseProfile(
  profile: ProfilePoint[],
  frame: HeightFrame
): FieldLayer[] {
  const points = profile
    .filter((point) => point.kind === "pressure_level")
    .map((point) => ({ point, height: heightIn(point, frame) }))
    .filter(
      (item): item is { point: ProfilePoint; height: number } =>
        item.height !== null
    )
    .sort((a, b) => a.height - b.height);

  const layers: FieldLayer[] = [];
  for (let i = 0; i < points.length - 1; i += 1) {
    const lower = points[i];
    const upper = points[i + 1];
    const spanThousands = (upper.height - lower.height) / 1000;
    if (spanThousands <= 0) continue;
    layers.push({
      height: (lower.height + upper.height) / 2,
      rate:
        (upper.point.temperature_c - lower.point.temperature_c) / spanThousands
    });
  }
  return layers;
}

function heightIn(point: ProfilePoint, frame: HeightFrame): number | null {
  const value = point[frame];
  return typeof value === "number" ? value : null;
}

/** Lapse rate at a height, linear between layer midpoints, clamped at the ends. */
export function sampleLapse(layers: FieldLayer[], height: number): number | null {
  if (layers.length === 0) return null;
  if (height <= layers[0].height) return layers[0].rate;
  const last = layers[layers.length - 1];
  if (height >= last.height) return last.rate;
  for (let i = 0; i < layers.length - 1; i += 1) {
    const low = layers[i];
    const high = layers[i + 1];
    if (height >= low.height && height <= high.height) {
      const span = high.height - low.height;
      const weight = span <= 0 ? 0 : (height - low.height) / span;
      return low.rate + (high.rate - low.rate) * weight;
    }
  }
  return last.rate;
}

function hexToRgb(hex: string): [number, number, number] {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16)
  ];
}

export interface FieldRequest {
  profiles: ProfilePoint[][];
  frame: HeightFrame;
  minHeight: number;
  maxHeight: number;
  samplesPerHour?: number;
  rows?: number;
}

/**
 * Render the field to a data URL, or null where no canvas exists.
 *
 * Returning null rather than throwing lets the caller keep the flat-rectangle
 * rendering as a fallback, so a server-rendered or test environment still
 * produces a correct — if blockier — chart.
 */
export function renderStabilityField({
  profiles,
  frame,
  minHeight,
  maxHeight,
  samplesPerHour = 8,
  rows = 220
}: FieldRequest): string | null {
  if (profiles.length === 0 || maxHeight <= minHeight) return null;
  if (typeof document === "undefined") return null;

  const canvas = document.createElement("canvas");
  const columns = Math.max(profiles.length * samplesPerHour, 2);
  canvas.width = columns;
  canvas.height = rows;
  const context = canvas.getContext("2d");
  if (!context) return null;

  const layered = profiles.map((profile) => lapseProfile(profile, frame));
  if (layered.every((layers) => layers.length === 0)) return null;

  const image = context.createImageData(columns, rows);
  const cache = new Map<string, [number, number, number]>();

  for (let row = 0; row < rows; row += 1) {
    // Row 0 is the top of the plot, which is the greatest height.
    const height =
      maxHeight - ((row + 0.5) / rows) * (maxHeight - minHeight);

    for (let column = 0; column < columns; column += 1) {
      const position = ((column + 0.5) / columns) * profiles.length - 0.5;
      const left = Math.min(
        Math.max(Math.floor(position), 0),
        profiles.length - 1
      );
      const right = Math.min(left + 1, profiles.length - 1);
      const weight = Math.min(Math.max(position - left, 0), 1);

      const lowRate = sampleLapse(layered[left], height);
      const highRate = sampleLapse(layered[right], height);
      const rate =
        lowRate === null
          ? highRate
          : highRate === null
            ? lowRate
            : lowRate + (highRate - lowRate) * weight;

      const offset = (row * columns + column) * 4;
      if (rate === null) {
        image.data[offset + 3] = 0;
        continue;
      }

      const color = stabilityBand(rate).color;
      let rgb = cache.get(color);
      if (!rgb) {
        rgb = hexToRgb(color);
        cache.set(color, rgb);
      }
      image.data[offset] = rgb[0];
      image.data[offset + 1] = rgb[1];
      image.data[offset + 2] = rgb[2];
      image.data[offset + 3] = 255;
    }
  }

  context.putImageData(image, 0, 0);
  return canvas.toDataURL("image/png");
}
